type DataLayerEntry = Record<string, unknown>;

type GoogleTagManagerWindow = Window & {
  dataLayer?: DataLayerEntry[];
};

const SCRIPT_ID = 'google-tag-manager-script';
const CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]+$/i;

// Translate shared analytics names to the custom events expected by the web GTM container.
const GTM_EVENT_NAMES: Record<string, string> = {
  login_success: 'user_logged_in',
  sign_up_success: 'user_signed_up',
};

// Keep this list explicit: analytics events can contain personal information.
// Only properties listed here are made available to tags in GTM.
const SAFE_EVENT_PROPERTIES: Record<string, readonly string[]> = {
  app_started: [],
  login_success: ['provider'],
  sign_up_success: ['provider'],
  paywall_opened: ['source'],
  paywall_closed: ['action'],
  offre_defricheur_activated: [],
  offre_trial_activated: [],
};

let enabled = false;
let lastPagePath: string | undefined;

export function initializeGoogleTagManager(containerId?: string) {
  const normalizedContainerId = containerId?.trim();
  if (!normalizedContainerId || !CONTAINER_ID_PATTERN.test(normalizedContainerId)) return;

  const googleTagManagerWindow = window as GoogleTagManagerWindow;
  googleTagManagerWindow.dataLayer ??= [];
  enabled = true;

  if (document.getElementById(SCRIPT_ID)) return;

  googleTagManagerWindow.dataLayer.push({
    'gtm.start': Date.now(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(normalizedContainerId)}`;
  document.head.appendChild(script);
}

export function pushGoogleTagManagerEvent(event: string, properties?: unknown) {
  if (!enabled) return;

  const allowedProperties = SAFE_EVENT_PROPERTIES[event];
  if (!allowedProperties) return;

  const dataLayerEntry: DataLayerEntry = { event: GTM_EVENT_NAMES[event] ?? event };
  if (isRecord(properties)) {
    for (const property of allowedProperties) {
      if (properties[property] !== undefined) dataLayerEntry[property] = properties[property];
    }
  }

  const googleTagManagerWindow = window as GoogleTagManagerWindow;
  googleTagManagerWindow.dataLayer?.push(dataLayerEntry);
}

export function pushGoogleTagManagerPageView(pageName: string, pagePath: string, utmParams?: Record<string, string>) {
  if (!enabled) return;

  const normalizedPagePath = pagePath.split(/[?#]/, 1)[0] || '/';
  if (normalizedPagePath === lastPagePath) return;
  lastPagePath = normalizedPagePath;

  const safeUtmParams = Object.fromEntries(
    Object.entries(utmParams ?? {}).filter(([key]) => /^utm_[a-z0-9_]+$/i.test(key)),
  );
  const utmQuery = Object.entries(safeUtmParams)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');
  const pageLocation = `${window.location.origin}${normalizedPagePath}${utmQuery ? `?${utmQuery}` : ''}`;

  const googleTagManagerWindow = window as GoogleTagManagerWindow;
  googleTagManagerWindow.dataLayer?.push({
    event: 'virtual_page_view',
    page_name: pageName,
    page_path: normalizedPagePath,
    page_location: pageLocation,
    ...safeUtmParams,
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
